import { HTTPError } from "ky"
import { ErrorCode } from "@/constants/errors"

export interface ApiErrorData {
  error_code?: number
  message?: string
  data?: unknown
}

export type ApiErrorWithData = HTTPError & { data?: ApiErrorData }
export type TranslateFn = (key: string) => string
export type ApiErrorKind = "permission_denied" | "api" | "http" | "unknown"
export interface NormalizedApiError {
  kind: ApiErrorKind
  status: number | null
  errorCode: number | null
  message: string | null
  data: unknown
  errorData: ApiErrorData | null
  messageKey: string | null
  raw: unknown
}

export interface ApiErrorPresentationProps {
  variant?: "destructive"
  className?: string
}

export interface UserFacingApiErrorToastOptions {
  title: string
  fallbackDescription: string
}

export interface UserFacingApiErrorToastProps extends ApiErrorPresentationProps {
  title: string
  description: string
}

export const permissionDeniedToastClassName =
  "border-amber-200 bg-amber-50 text-amber-950 shadow-md dark:border-amber-800/60 dark:bg-amber-950/60 dark:text-amber-100"
export const validationErrorToastClassName =
  "border-amber-200 bg-amber-50 text-amber-950 shadow-md dark:border-amber-800/60 dark:bg-amber-950/60 dark:text-amber-100"

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const isApiErrorData = (value: unknown): value is ApiErrorData => {
  if (!isRecord(value)) return false
  return (
    typeof value.error_code === "number" ||
    typeof value.message === "string" ||
    "data" in value
  )
}

const getErrorResponseStatus = (error: unknown): number | null => {
  if (error instanceof HTTPError) return error.response.status
  if (!isRecord(error)) return null
  const response = error.response
  if (!isRecord(response)) return null
  return typeof response.status === "number" ? response.status : null
}

const withFallbackPermissionDeniedCodeFor403 = (
  errorData: ApiErrorData | null,
  status: number | null
): ApiErrorData | null => {
  if (status !== 403) return errorData
  return errorData ?? { error_code: ErrorCode.SYS_PERMISSION_DENIED }
}

const extractApiErrorData = (error: unknown): ApiErrorData | null => {
  const status = getErrorResponseStatus(error)
  if (error instanceof HTTPError || (isRecord(error) && "response" in error)) {
    const data = (error as { data?: unknown }).data
    return withFallbackPermissionDeniedCodeFor403(
      isApiErrorData(data) ? data : null,
      status
    )
  }
  if (!isRecord(error)) return null
  const data = (error as { data?: unknown }).data
  if (isApiErrorData(data)) return data
  return isApiErrorData(error) ? error : null
}

const getErrorMessage = (
  error: unknown,
  errorData: ApiErrorData | null
): string | null => {
  if (errorData?.message) return errorData.message
  if (error instanceof Error && error.message) return error.message
  if (typeof error === "string" && error) return error
  return null
}

export function normalizeApiError(error: unknown): NormalizedApiError {
  const status = getErrorResponseStatus(error)
  const errorData = extractApiErrorData(error)
  const errorCode =
    typeof errorData?.error_code === "number" ? errorData.error_code : null
  const isPermissionDenied =
    status === 403 || errorCode === ErrorCode.SYS_PERMISSION_DENIED
  const kind: ApiErrorKind = isPermissionDenied
    ? "permission_denied"
    : errorCode != null
      ? "api"
      : status != null
        ? "http"
        : "unknown"

  return {
    kind,
    status,
    errorCode,
    message: getErrorMessage(error, errorData),
    data: errorData?.data,
    errorData,
    messageKey: isPermissionDenied ? "common.noPermission" : null,
    raw: error,
  }
}

export function getApiErrorData(error: unknown): ApiErrorData | null {
  return normalizeApiError(error).errorData
}

export function isPermissionDeniedApiError(error: unknown): boolean {
  return normalizeApiError(error).kind === "permission_denied"
}

export function getPermissionDeniedTranslationKey(
  error: unknown
): string | null {
  const normalizedError = normalizeApiError(error)
  return normalizedError.kind === "permission_denied"
    ? normalizedError.messageKey
    : null
}

export function isPermissionDeniedApiErrorData(
  errorData?: ApiErrorData | null
): boolean {
  return normalizeApiError(errorData).kind === "permission_denied"
}

// Use only when the caller intentionally has parsed API payload/response data.
// It cannot see HTTP status, so catch blocks should prefer getUserFacingApiErrorMessage.
export function getApiErrorPayloadMessage(
  errorData: ApiErrorData | null | undefined,
  t: TranslateFn,
  fallback: string
): string {
  const normalizedError = normalizeApiError(errorData)
  return (
    (normalizedError.messageKey && t(normalizedError.messageKey)) ||
    normalizedError.message ||
    fallback
  )
}

// Default helper for user-facing API errors. Keep the original error object so
// status-based semantics, especially HTTP 403 permission denied, are preserved.
export function getUserFacingApiErrorMessage(
  error: unknown,
  t: TranslateFn,
  fallback: string
): string {
  const normalizedError = normalizeApiError(error)
  return (
    (normalizedError.messageKey && t(normalizedError.messageKey)) ||
    normalizedError.message ||
    fallback
  )
}

export function getApiErrorPresentationProps(
  error: unknown
): ApiErrorPresentationProps {
  const normalizedError = normalizeApiError(error)
  if (normalizedError.kind === "permission_denied") {
    return { className: permissionDeniedToastClassName }
  }
  if (normalizedError.errorCode === ErrorCode.SYS_VALIDATION_ERROR) {
    return { className: validationErrorToastClassName }
  }
  return { variant: "destructive" }
}

export function getUserFacingApiErrorToastProps(
  error: unknown,
  t: TranslateFn,
  options: UserFacingApiErrorToastOptions
): UserFacingApiErrorToastProps {
  return {
    title: options.title,
    description: getUserFacingApiErrorMessage(
      error,
      t,
      options.fallbackDescription
    ),
    ...getApiErrorPresentationProps(error),
  }
}
